import React, { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import { Modal } from "react-bootstrap";
import CommonTable from "../../Components/CommonTable";
import ShowVendorsDetailsData from "./ShowVendorsDetailsData";
import { third_party_monitored_vendors } from "../../services";

const cmCharCode = ['A', 'B', 'C', 'D', 'F']

function VendorsByBusinessImpact({ data = {}, isWide = false }) {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [selectedDetails, setSelectedDetails] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showVendorDetails, setShowVendorDetails] = useState(false);
    const [vendorDetailsTargetOrgId, setVendorDetailsTargetOrgId] = useState(null);
    const [vendorDetailsName, setVendorDetailsName] = useState('');
    const [resolvingVendor, setResolvingVendor] = useState(false);

    // Handle resize for responsiveness
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // 🔹 Transform API heatmap data into flat array
    const transformedData = useMemo(() => {
        const rows = ["Critical", "High", "Medium", "Low"];
        const columns = [
            "Very Unlikely",
            "Unlikely",
            "Possible",
            "Likely",
            "Very Likely",
        ];

        const flatData = [];

        rows.forEach((row) => {
            columns.forEach((col) => {
                const value = data?.heatmap?.[row]?.[col] ?? 0; // read from heatmap
                flatData.push({ row, col, value });
            });
        });

        return flatData;
    }, [data]);

    // 🔹 Static color mapping per row + column
    const getCellColor = (row, col) => {
        if (col === "Very Unlikely") return "#19A07E";
        if (col === "Unlikely")
            return row === "Critical" || row === "High" ? "#D9BF21" : "#19A07E";
        if (col === "Possible")
            return row === "Critical" || row === "High" ? "#DE9E30" : "#D9BF21";
        if (col === "Likely")
            return row === "Critical" || row === "High" ? "#E56A49" : "#DE9E30";
        if (col === "Very Likely")
            return row === "Critical" || row === "High" ? "#D24646" : "#E56A49";
        return "#333";
    };

    // 🔹 Handle cell click → open modal with filtered details
    const handleCellClick = (row, col) => {
        const filtered = (data?.details || []).filter(
            (d) => d.impact_band === row && d.likelihood_band === col
        );
        setSelectedDetails(filtered);
        setShowModal(true);
    };

    // Draw heatmap
    useEffect(() => {
        if (!containerWidth || transformedData.length === 0) return;

        const margin = { top: 60, right: 30, bottom: 60, left: 60 };
        const totalHeight = 500;
        const width = containerWidth;
        const height = totalHeight;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        svg
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet");

        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const columns = [
            "Very Unlikely",
            "Unlikely",
            "Possible",
            "Likely",
            "Very Likely",
        ];
        const rows = ["Critical", "High", "Medium", "Low"];

        const gridWidth = width - margin.left - margin.right;
        const gridHeight = height - margin.top - margin.bottom;
        const cellSize = Math.min(gridWidth / columns.length, gridHeight / rows.length);

        // Draw cells
        rows.forEach((row, i) => {
            columns.forEach((col, j) => {
                const cellData = transformedData.find(
                    (d) => d.row === row && d.col === col
                );
                const value = cellData ? cellData.value : 0;

                chart
                    .append("rect")
                    .attr("x", j * cellSize)
                    .attr("y", i * cellSize)
                    .attr("width", cellSize)
                    .attr("height", cellSize)
                    .attr("fill", getCellColor(row, col))
                    .attr("stroke", "#1e1e2f")
                    .style("cursor", "pointer")
                    .on("click", () => handleCellClick(row, col));

                chart
                    .append("text")
                    .attr("x", j * cellSize + cellSize / 2)
                    .attr("y", i * cellSize + cellSize / 2)
                    .attr("dy", ".35em")
                    .attr("text-anchor", "middle")
                    .attr("fill", "#fff")
                    .style("pointer-events", "none")
                    .style("font-size", "16px")
                    .style("font-weight", "700")
                    .text(value);
            });
        });

        // X-axis labels
        const screenWidth = window.innerWidth;
        let fontSize = 14; // Default

        if (screenWidth <= 1368) {
            fontSize = 9;
        } else if (screenWidth <= 1024) {
            fontSize = 10;
        }

        chart
            .selectAll(".x-label")
            .data(columns)
            .enter()
            .append("text")
            .attr("x", (d, i) => i * cellSize + cellSize / 2)
            .attr("y", rows.length * cellSize + 25)
            .attr("text-anchor", "middle")
            .attr("fill", "#fff")
            .style("font-size", `${fontSize}px`)
            .text((d, i) => `${d}`);

        // Y-axis labels
        chart
            .selectAll(".y-label")
            .data(rows)
            .enter()
            .append("text")
            .attr("x", -10)
            .attr("y", (d, i) => i * cellSize + cellSize / 2)
            .attr("text-anchor", "end")
            .attr("alignment-baseline", "middle")
            .attr("fill", "#fff")
            // .style("font-size", "14")
            .style("font-size", `${fontSize}px`)
            .text((d) => d);


        // Legend
        const legendWidth = 480;
        const legendHeight = 4;
        const legendX = (width - legendWidth) / 2;

        const defs = svg.append("defs");
        const gradient = defs
            .append("linearGradient")
            .attr("id", "legend-gradient")
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "100%")
            .attr("y2", "0%");

        const legendColors = [
            { offset: "0%", color: "#19A07E" },
            { offset: "25%", color: "#D9BF21" },
            { offset: "50%", color: "#DE9E30" },
            { offset: "75%", color: "#E56A49" },
            { offset: "100%", color: "#D24646" },
        ];

        legendColors.forEach((stop) => {
            gradient.append("stop").attr("offset", stop.offset).attr("stop-color", stop.color);
        });

        const legend = svg.append("g").attr("transform", `translate(${legendX}, ${20})`);
        legend
            .append("rect")
            .attr("width", legendWidth)
            .attr("height", legendHeight)
            .style("fill", "url(#legend-gradient)");
        legend
            .append("text")
            .attr("x", 0)
            .attr("y", -5)
            .attr("fill", "#fff")
            .text("Low")
            .style("font-size", "14");

        legend
            .append("text")
            .attr("x", legendWidth)
            .attr("y", -5)
            .attr("text-anchor", "end")
            .attr("fill", "#fff")
            .text("High")
            .style("font-size", "14");
    }, [transformedData, containerWidth]);

    // Table headers
    const headers = [
        "Vendor Name",
        "Likelihood",
        "Likelihood Score",
        "Impact",
        "Impact Score",
    ];

    // Table rows
    const rows = selectedDetails?.map((item) => ({
        data: [
            <div
                className="dark_blue_text cursor_pointer"
                style={{ opacity: resolvingVendor ? 0.5 : 1, pointerEvents: resolvingVendor ? 'none' : 'auto' }}
                onClick={async () => {
                    setResolvingVendor(true);
                    setVendorDetailsName(item?.vendor_name);
                    try {
                        const response = await third_party_monitored_vendors({ relation_id: item?.relation_id });
                        const vendors = response?.statusCode?.response_data?.data || [];
                        const matched = vendors.find(v => v.id === item?.relation_id || v.relation_id === item?.relation_id);
                        const targetOrgId = matched?.target_organization_id;
                        setVendorDetailsTargetOrgId(targetOrgId);
                        setShowVendorDetails(true);
                    } catch (e) {
                        console.error('Failed to resolve vendor:', e);
                    } finally {
                        setResolvingVendor(false);
                    }
                }}
            >
                {item?.vendor_name || "-"}
            </div>,
            item?.likelihood_band || "-",
            item?.likelihood_score || "-",
            item?.impact_band || "-",
            item?.impact_score || "-",
        ],
    }));

    return (
        <>
        <div className={`vendors-impact-graph ${isWide ? "vendors-impact-graph_2" : ""}`} ref={containerRef}>
            <h3 className="font_18 font_600 color_white">Vendors by Business Impact</h3>
            <svg ref={svgRef} className="responsive-svg d-flex w-100" />

            {/* Modal for details */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                centered
                className="finding_cstm_modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Vendor Details{" "}
                        {selectedDetails?.length > 0 && `(${selectedDetails.length})`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedDetails?.length > 0 ? (
                        <CommonTable headers={headers} rows={rows} />
                    ) : (
                        <p className="text-center color_white pt-5 mb-5 font_16">No Vendor Details Available.</p>
                    )}
                </Modal.Body>
            </Modal>
        </div>

            {showVendorDetails &&
                <ShowVendorsDetailsData
                    show={showVendorDetails}
                    onHide={() => setShowVendorDetails(false)}
                    id={vendorDetailsTargetOrgId}
                    name={vendorDetailsName}
                />
            }
        </>
    );
}

export default VendorsByBusinessImpact;
